/**
 * RegistroCapas — registro/CRUD de capas por tipo (operativa | informativa).
 *
 * - Operativa (Predios): lote-delete vía DELETE /v1/capas/:id →
 *   captura_delete_capa_lote (omite predios contratados y los reporta). El
 *   "deshacer" de una carga reciente = borrar la capa en lote.
 * - Informativa (Referencia): editar meta (nombre/visibilidad) + borrar
 *   (cascade) vía PATCH/DELETE /v1/capas/:id.
 *
 * Lecturas vía useCapasRegistry (RLS); mutaciones vía BFF (service_role).
 */
import { Fragment, useState } from 'react';
import { bffFetch, invalidateCache, useCapasRegistry, type CapaRegistroRow } from '@aosenuma/lib-supabase';
import { showToast } from '@aosenuma/ui';
import { CapaDetalle } from './CapaDetalle';

interface LoteDeleteResult {
  deleted: number;
  blocked: { parcela_id: string }[];
  capa_deleted: boolean;
  r2_files_attempted?: number;
}

export function RegistroCapas({ tipo }: { tipo: 'operativa' | 'informativa' }) {
  const { data, loading, error } = useCapasRegistry();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const capas = data.filter((c) => c.tipo === tipo);
  const anyBusy = busyId !== null;

  function refreshAll() {
    // invalidateCache refetcha a todos los suscriptores de estas keys vía el
    // cache bus (incl. este registro y los mapas) — sin refetch() redundante.
    invalidateCache(['capas_registry', 'capas_informativas', 'parcelas_privadas', 'parcelas_publicas']);
  }

  async function borrarOperativa(c: CapaRegistroRow) {
    if (!confirm(`¿Borrar la capa "${c.nombre}" y sus ${c.count} predio(s)?\n\nLos predios con contrato firmado se omiten y se reportan. Esta acción no se puede deshacer.`)) return;
    setBusyId(c.id);
    try {
      const res = await bffFetch<LoteDeleteResult>(`/v1/capas/${c.id}`, { method: 'DELETE' });
      refreshAll();
      const blocked = res.blocked?.length ?? 0;
      if (blocked > 0) {
        showToast(`${res.deleted} borrado(s); ${blocked} omitido(s) por contrato: ${res.blocked.map((b) => b.parcela_id).join(', ')}.`, { variant: 'error', ms: 8000 });
      } else {
        showToast(`Capa "${c.nombre}" borrada (${res.deleted} predio(s)).`, { variant: 'success' });
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al borrar la capa.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function borrarInformativa(c: CapaRegistroRow) {
    if (!confirm(`¿Borrar la capa "${c.nombre}" y sus ${c.count} geometría(s) de referencia?`)) return;
    setBusyId(c.id);
    try {
      await bffFetch(`/v1/capas/${c.id}`, { method: 'DELETE' });
      refreshAll();
      showToast(`Capa "${c.nombre}" borrada.`, { variant: 'success' });
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al borrar la capa.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function patchCapa(c: CapaRegistroRow, body: Record<string, unknown>) {
    setBusyId(c.id);
    try {
      await bffFetch(`/v1/capas/${c.id}`, { method: 'PATCH', body });
      refreshAll();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al editar la capa.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function toggleVisible(c: CapaRegistroRow) {
    await patchCapa(c, { visible: !c.visible });
  }

  function startEdit(c: CapaRegistroRow) {
    setEditId(c.id);
    setEditNombre(c.nombre);
  }
  async function saveEdit(c: CapaRegistroRow) {
    const n = editNombre.trim();
    if (!n) { showToast('El nombre no puede quedar vacío.', { variant: 'error' }); return; }
    if (n !== c.nombre) await patchCapa(c, { nombre: n });
    setEditId(null);
  }

  if (loading) return <p className="panel-sub">Cargando capas…</p>;
  if (error) return <p className="panel-sub" style={{ color: 'var(--error, #b56556)' }}>Error: {error.message}</p>;
  if (capas.length === 0) {
    return (
      <p className="panel-sub">
        {tipo === 'operativa'
          ? 'No hay capas de predios todavía. Crea una desde la pestaña Subir.'
          : 'No hay capas de referencia todavía. Crea una desde la pestaña Subir.'}
      </p>
    );
  }

  return (
    <div className="panel-section" style={{ overflowX: 'auto' }}>
      <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>{tipo === 'operativa' ? 'Predios' : 'Geometrías'}</th>
            {tipo === 'informativa' && <><th>Tipo</th><th>Visible</th></>}
            <th>Creada por</th>
            <th>Fecha</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {capas.map((c) => {
            const busy = busyId === c.id;
            return (
              <Fragment key={c.id}>
              <tr style={{ opacity: busy ? 0.5 : 1 }}>
                <td>
                  {editId === c.id ? (
                    <input value={editNombre} maxLength={200} onChange={(e) => setEditNombre(e.target.value)} style={{ width: 200 }} autoFocus />
                  ) : (
                    <strong>{c.nombre}</strong>
                  )}
                  {c.archivo_origen && editId !== c.id && (
                    <span style={{ display: 'block', fontSize: 11, opacity: 0.6 }}>{c.archivo_origen}</span>
                  )}
                </td>
                <td>{c.count}</td>
                {tipo === 'informativa' && (
                  <>
                    <td>
                      {c.estilo?.color && (
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c.estilo.color, marginRight: 6 }} />
                      )}
                      {c.subtipo ?? <em style={{ opacity: 0.6 }}>—</em>}
                    </td>
                    <td>
                      <input type="checkbox" checked={c.visible} disabled={anyBusy} onChange={() => void toggleVisible(c)} title="Mostrar en el mapa" />
                    </td>
                  </>
                )}
                <td style={{ fontSize: 12 }}>{c.created_by_email ?? '—'}</td>
                <td style={{ fontSize: 12 }}>{new Date(c.created_at).toLocaleDateString('es-MX')}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {tipo === 'informativa' && (
                    editId === c.id ? (
                      <>
                        <button type="button" onClick={() => void saveEdit(c)} disabled={anyBusy} style={{ marginRight: 6 }}>Guardar</button>
                        <button type="button" onClick={() => setEditId(null)} disabled={anyBusy}>Cancelar</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => startEdit(c)} disabled={anyBusy} style={{ marginRight: 6 }}>Renombrar</button>
                    )
                  )}
                  {tipo === 'informativa' && editId !== c.id && (
                    <button type="button" onClick={() => setExpandedId((id) => (id === c.id ? null : c.id))} disabled={anyBusy} style={{ marginRight: 6 }}>
                      {expandedId === c.id ? 'Ocultar' : 'Ver elementos'}
                    </button>
                  )}
                  {editId !== c.id && (
                    <button
                      type="button"
                      onClick={() => void (tipo === 'operativa' ? borrarOperativa(c) : borrarInformativa(c))}
                      disabled={anyBusy}
                      style={{ color: 'var(--error, #b56556)' }}
                    >
                      {tipo === 'operativa' ? 'Borrar lote' : 'Borrar'}
                    </button>
                  )}
                </td>
              </tr>
              {tipo === 'informativa' && expandedId === c.id && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <CapaDetalle capaId={c.id} />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
